import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Container, Row, Col, Card } from 'react-bootstrap'
import { AccordionSection, AccordionCard } from '../../accordion'
import { CardRowItem } from '../../cardRowItem'
import { SBText } from 'application/components/styledComponents/text'
import { Emission, PrimaryButton, FeatureBlock } from '@mmsg/louie'
import { Modal } from 'react-bootstrap'
import { SummarySheet } from '../pdf/summarySheet'
import { SummarySheetMX } from '../pdf/summarySheetMX'
import { capitalizeOnlyFirstLetter, formatCurrency, IsSpecialEmployer } from 'application/helpers'
import Tooltip from '../../tooltip'

import { ImageBase64 } from '../../image/image'
import * as S from './estimateDetailElements'
import * as E from './carSectionElements'
import { Icon } from 'RemServ/icons'
import { theme as MaxxiaTheme } from 'Maxxia/theme'
import { theme as RemServTheme } from 'RemServ/theme'
import { EstimateDetailStaticData } from './estimateDetailStaticData'
import { isBrandMX, isBrandRS } from 'application/constants/globals'

const CarSection = ({
  data: {
    brand,
    year,
    make,
    family,
    engineSize,
    fuelType,
    transmission,
    style,
    variant,
    bodyColour,
    trimColour,
    onRoadcost,
    priveawayPrice,
    amTotalExGST,
    dealerOptionTotal,
    vehicleAccessories = [],
    tpoTotalExGST,
    listedPrice,
    luxuryCarTax,
    dealerDiscount,
    deliveryFee,
    procurementFee,
    vehicleAccessoriesNames,
    accesoriesByYou,
    gstCreditClaimable,
    totalVehicleExGST,
    totalExGSTNoVehicle,
    totalVehicleGST,
    vehicleId,
    vehicleImage,
    vehicleDescription,
    opportunityId,
    vehicleType,
    thirdPartyOption,
    payload,
    cylinders,
    retailPrice,
    cO2EmissionsPerKM,
  },
  applicationRole,
  expandAccordion,
  afterMarketAccessories,
  novatedLeaseData,
  pdfViewClick
}) => {
  const isGov = useSelector((state) => state.user.isGov)
  const employeeName = useSelector((state) => state.user.firstName + ' ' + state.user.lastName)

  const bpFC = useSelector((state) => state.opportunity.bpFuelProvider)
  const caltexFC = useSelector((state) => state.opportunity.caltexFuelProvider)
  const shellFC = useSelector((state) => state.opportunity.shellFuelProvider)

  let fuelCards = []
  if (bpFC)
     fuelCards.push('BP')
  if (caltexFC)
     fuelCards.push('Ampol')
  if (shellFC)
     fuelCards.push('Shell')
    
  const employeeNumber = useSelector((state) => state.user.employer.payrollId)
  const payFrequency = useSelector((state) => state.user.employer.payFrequency)
  const aftermarketOrderLines =
    afterMarketAccessories && afterMarketAccessories.aftermarketOrderLines

  const accessoriesByYou =
    thirdPartyOption && thirdPartyOption.thirdPartyOptionLines

  let accessories = []
  if(accessoriesByYou) {accessories.push(accessoriesByYou.map((x) => x.description))}
  const vehicleAccessory = useSelector((state) => state.individualEstimate.estimate && 
      state.individualEstimate.estimate.vehicle && 
      state.individualEstimate.estimate.vehicle.vehicleAccessories)
  if(vehicleAccessory) {accessories.push(vehicleAccessory.map((x) => x.name))}  
  aftermarketOrderLines && aftermarketOrderLines.forEach((item) => (accessories.push( item.description)))

  
  const showRecommendedRetailPrice = retailPrice > priveawayPrice

  const recommendedRetailPriceTooltipText = EstimateDetailStaticData(
    'RRPTooltip',
    {
      vehicleDescription: vehicleDescription,
      retailPrice: formatCurrency(retailPrice),
    }
  )
  const carSectionSubText =
    cO2EmissionsPerKM != null ? (
      <p>
        Take a look at your car specs, including CO<sub>2</sub> emissions.
      </p>
    ) : (
      <p>Take a look at your car specs.</p>
    )

  const emissionAttribute = (
    <p>
      CO<sub>2</sub> emissions
    </p>
  )

  const [showSummarySheet, setShowSummarySheet] = useState(false)
  const toggleviewSummarySheet = () => {
    setShowSummarySheet(!showSummarySheet)
  }
  const isExGST = useSelector((state) => state.user.employer.employerGRS.gstExclusiveDeductionsCode.toLowerCase() === 'yes')

  let summarySheetData = {
    ...novatedLeaseData.proposalEstimate,
    employeeName,
    employeeNumber,
    fuelCards,
    isExGST,
    payFrequency,
    accessories
  }
  

  const CarAttributeRow = ({ title, id, value }) => {
    if (value == null) return <></>
    return (
      <Row>
        <S.CardText bold id={id + '_title'}>
          {title}
        </S.CardText>
        <S.CardValue id={id + '_value'}>{value}</S.CardValue>
      </Row>
    )
  }

  return (
    <>
      <Modal show={showSummarySheet} onHide={toggleviewSummarySheet} size="lg">
        <Modal.Header
          closeButton
          style={{
            background: 'transparent',
            borderBottom: 'none',
            paddingBottom: 0,
          }}
        ></Modal.Header>
        <Modal.Body>
          <div>
            {isBrandMX && <SummarySheetMX summarySheetData={summarySheetData}/>}
            {isBrandRS && <SummarySheet novatedLeaseData={novatedLeaseData} exGst={true} />}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'start' }}>
          <PrimaryButton extendInMob onClick={pdfViewClick}>
            View PDF
          </PrimaryButton>
          <PrimaryButton
            extendInMob
            invertColor
            onClick={toggleviewSummarySheet}
          >
            Close
          </PrimaryButton>
        </Modal.Footer>
      </Modal>

      <Container className="section mt-5 mb-5">
        <E.AllEstimatesDisclaimer>
          All estimates presented are for illustrative purposes only and are
          only current as at the date generated. Estimates shown may not be
          valid after the generation date.
        </E.AllEstimatesDisclaimer>
        <E.carTitle>
          {vehicleId && (
            <span id="carsection.vehicle">
              {year} {make} {family}
            </span>
          )}
          {!vehicleId && (
            <span id="carsection.vehicleDesc">{vehicleDescription}</span>
          )}
        </E.carTitle>
        {vehicleId && (
          <E.carDescriptionDiv className="" id="carsection.vehicleDesc">
            {vehicleDescription}
          </E.carDescriptionDiv>
        )}
        <div className="car-image car-image-large mb-3 pb-0">
          {vehicleId && vehicleImage && (
            <ImageBase64
              ImageBase64Encoded={vehicleImage.base64Image}
              id="carsection.vehicleImage"
            />
          )}
          {(!vehicleId || !vehicleImage) && (
            <img
              src="/images/car-image-default.svg"
              alt=""
              id="carsection.vehicleImageDefault"
            />
          )}
        </div>
        <E.indicativeImgText>Image is indicative only</E.indicativeImgText>

        {(isGov || IsSpecialEmployer('REMEQ') || IsSpecialEmployer('VICPO')) && (
          <div className="mt-3 mb-3 mx-auto text-center">
            <PrimaryButton invertColor onClick={toggleviewSummarySheet}>
              <Icon iconType="Sheet" size={1} />
              View summary sheet
            </PrimaryButton>
          </div>
        )}

        {vehicleId && (
          <Card className="mb-13rem card-shadow">
            <Card.Header className="border-bottom-0">
              <FeatureBlock
                infoHeader={
                  global.config.IsBrandMX
                    ? 'Your car details'
                    : 'The car details'
                }
                info1Content={
                  (engineSize === null ? '' : engineSize + ' ') +
                  fuelType +
                  ' engine'
                }
                info2Content={transmission}
                info3Content={style}
                theme={global.config.IsBrandMX ? MaxxiaTheme : RemServTheme}
              />
            </Card.Header>
            <Card.Body>
              <AccordionSection
                readerText="your car details"
                title={carSectionSubText}
                expandAccordion={expandAccordion}
                id="carsection.yourcardetailsaccordion"
              >
                <CarAttributeRow
                  title="Series"
                  value={variant}
                  id="carsection.series"
                />
                <CarAttributeRow
                  title="Engine"
                  value={engineSize}
                  id="carsection.engine"
                />
                <CarAttributeRow
                  title="Transmission"
                  value={transmission}
                  id="carsection.transmission"
                />
                <CarAttributeRow
                  title="Body"
                  value={style}
                  id="carsection.body"
                />
                <CarAttributeRow
                  title="Colour"
                  value={bodyColour}
                  id="carsection.colour"
                />
                <CarAttributeRow
                  title="Interior colour"
                  value={trimColour}
                  id="carsection.interiorcolour"
                />
                <CarAttributeRow
                  title="Fuel"
                  value={capitalizeOnlyFirstLetter(fuelType)}
                  id="carsection.fuel"
                />
                <CarAttributeRow
                  title="Cylinders"
                  value={cylinders}
                  id="carsection.cylinders"
                />
                <CarAttributeRow
                  title="Payload"
                  value={payload}
                  id="carsection.payload"
                />
                {cO2EmissionsPerKM != null && (
                  <div>
                    <CarAttributeRow
                      title={emissionAttribute}
                      value={cO2EmissionsPerKM + ' g/km'}
                      id="carsection.emissions"
                    />
                    <S.EmissionContainer>
                      <Emission value={cO2EmissionsPerKM} />
                    </S.EmissionContainer>
                  </div>
                )}
              </AccordionSection>
            </Card.Body>
          </Card>
        )}

        <Card className="mb-13rem print-page-break-before card-shadow">
          {/*If the novated leasing price is more than or equal to the retail price*/}
          {!showRecommendedRetailPrice && (
            <Card.Header className="border-bottom-0 d-flex justify-content-between">
              <S.CardHeader>Your {brand} Novated Leasing price</S.CardHeader>
              <S.CardHeaderValue id="carsection.novatedleasingprice1">
                {formatCurrency(priveawayPrice)}
              </S.CardHeaderValue>
            </Card.Header>
          )}
          {/*If the novated leasing price is less than the retail price*/}
          {showRecommendedRetailPrice && (
            <>
              <Card.Header className="border-bottom-0 d-flex justify-content-between">
                <S.CardHeader>
                  Your {brand} Novated Leasing price<sup>†</sup>
                </S.CardHeader>
                <E.RRPrice id="carsection.novatedleasingprice2">
                  {formatCurrency(priveawayPrice)}
                </E.RRPrice>
              </Card.Header>
              <Card.Header className="pt-0 border-bottom-0  d-flex justify-content-between">
                <div>
                  <E.RRPSubText>
                    {global.config.isBrandMX
                      ? 'Recommended Retail Price'
                      : 'Estimated Total Retail Price'}
                    <sup>‡</sup>
                  </E.RRPSubText>
                  <Tooltip
                    header="Recommended Retail Price"
                    text={recommendedRetailPriceTooltipText}
                    id="carsection.recommededretailpricetooltip"
                  />
                </div>
                <E.RRSubTextPrice id="carsection.recommededretailprice">
                  {formatCurrency(retailPrice)}
                </E.RRSubTextPrice>
              </Card.Header>
            </>
          )}

          <Card.Body>
            <AccordionSection
              readerText="Your Maxxia Novated Leasing Price"
              title={
                global.config.isBrandRS
                  ? 'Unpack the car’s value, including accessories and on roads.'
                  : 'Unpack your car’s value, including accessories.'
              }
              expandAccordion={expandAccordion}
              id="carsection.novatedleasingpriceaccordion"
            >
              <>
                <Row>
                  <E.CarPriceDiv>Car price</E.CarPriceDiv>
                </Row>
                {listedPrice > 0 && (
                  <Row>
                    <S.CardText>Listed price</S.CardText>
                    <S.CardValue id="carsection.listedprice">
                      {formatCurrency(listedPrice)}
                    </S.CardValue>
                  </Row>
                )}
                {vehicleId && dealerDiscount > 0 && (
                  <Row>
                    <Col className="text-positive">Fleet Discount</Col>
                    <Col
                      className="text-right text-positive"
                      id="carsection.fleetdiscount"
                    >
                      -{formatCurrency(dealerDiscount)}
                    </Col>
                  </Row>
                )}
                {vehicleId && deliveryFee > 0 && (
                  <Row>
                    <S.CardText>Delivery charges</S.CardText>
                    <S.CardValue id="carsection.deliverycharges">
                      {formatCurrency(deliveryFee)}
                    </S.CardValue>
                  </Row>
                )}
                {vehicleId && procurementFee > 0 && (
                  <Row>
                    <S.CardText>Procurement fee</S.CardText>
                    <S.CardValue id="carsection.procurementfee">
                      {formatCurrency(procurementFee)}
                    </S.CardValue>
                  </Row>
                )}
                {onRoadcost > 0 && (
                  <>
                    <Row>
                      <S.CardText>On roads</S.CardText>
                      <S.CardValue id="carsection.onroads">
                        {formatCurrency(onRoadcost)}
                      </S.CardValue>
                    </Row>

                    <Row>
                      <S.GreyTextDiv xs md={9} id="carsection.onroadinclusions">
                        {EstimateDetailStaticData('CarPriceIncluded')}
                      </S.GreyTextDiv>
                    </Row>
                  </>
                )}
                {luxuryCarTax > 0 && (
                  <Row>
                    <S.CardText>
                      <div className="d-flex ">
                        <span className="mr-2">Luxury car tax </span>
                        <Tooltip
                          header="Luxury car tax"
                          text="An Australian Government tax placed upon imported cars over a certain value. 33% tax is applied to the portion of the price that exceeds this value threshold. "
                          id="carsection.luxurycartaxtooltip"
                        />
                      </div>
                    </S.CardText>
                    <S.CardValue id="carsection.luxurycartax">
                      {formatCurrency(luxuryCarTax)}
                    </S.CardValue>
                  </Row>
                )}
                {((aftermarketOrderLines && aftermarketOrderLines.length > 0) ||
                  (accessoriesByYou && accessoriesByYou.length > 0) ||
                  (vehicleAccessories && vehicleAccessories.length > 0)) && (
                  <div>
                    <hr></hr>
                    <SBText size="18">ACCESSORIES</SBText>

                    {vehicleAccessories && vehicleAccessories.length > 0 && (
                      <div className="mt-3">
                        <SBText size="18" id="carsection.dealershipaccessories">
                          Arranged by Dealership
                        </SBText>
                        <div className="ml-3">
                          {vehicleAccessories.map((item, i) => (
                            <CardRowItem
                              subtitle2={item.name}
                              value={item.amount}
                              isCurrency="true"
                              rightTextcss="text-right"
                              id={
                                'carsection.dealershipaccessories' +
                                i.toString()
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {aftermarketOrderLines &&
                      aftermarketOrderLines.length > 0 && (
                        <div className="mt-3">
                          <SBText size="18" id="carsection.maxxiaaccessories">
                            Arranged by {global.config.brand}
                          </SBText>
                          <div className="ml-3">
                            {aftermarketOrderLines.map((item, i) => (
                              <CardRowItem
                                subtitle2={item.description}
                                value={item.priceIncGST / 1.1}
                                isCurrency="true"
                                rightTextcss="text-right"
                                id={
                                  'carsection.maxxiaaccessories' + i.toString()
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    {accessoriesByYou && accessoriesByYou.length > 0 && (
                      <SBText size="18" id="carsection.selfarrangedaccessories">
                        Arranged by you
                      </SBText>
                    )}
                    <div className="ml-3">
                      {accessoriesByYou &&
                        accessoriesByYou.map((item, i) => (
                          <CardRowItem
                            subtitle2={item.description}
                            value={item.amountExGST}
                            isCurrency="true"
                            rightTextcss="text-right"
                            id={
                              'carsection.selfarrangedaccessories' +
                              i.toString()
                            }
                          />
                        ))}
                    </div>
                    <div className="text-small">
                      (Accessories are all exclusive of GST for the purpose of
                      this estimate only.)
                    </div>
                  </div>
                )}

                <Row className="text-right border-top mx-0 pt-2 mt-3">
                  <S.CardText xs md={9} className="px-0">
                    Subtotal (excl GST)
                  </S.CardText>
                  <S.CardValue
                    className="px-0"
                    xs
                    md={3}
                    id="carsection.subtotalexclgst"
                  >
                    {vehicleId
                      ? formatCurrency(totalVehicleExGST)
                      : formatCurrency(totalExGSTNoVehicle)}
                  </S.CardValue>
                </Row>

                {totalVehicleGST > 0 && (
                  <Row className="text-right mx-0">
                    <S.CardText xs md={9} className="px-0">
                      GST
                    </S.CardText>
                    <S.CardValue xs md={3} className="px-0" id="carsection.gst">
                      {formatCurrency(totalVehicleGST)}
                    </S.CardValue>
                  </Row>
                )}
                {gstCreditClaimable > 0 && (
                  <Row className="text-right text-positive mx-0">
                    <Col xs md={9} className="px-0">
                      Upfront GST savings
                    </Col>
                    <Col
                      xs
                      md={3}
                      className="px-0"
                      id="carsection.upfrontgstsavings"
                    >
                      -{formatCurrency(gstCreditClaimable)}
                    </Col>
                  </Row>
                )}
                {priveawayPrice > 0 && (
                  <Row className="text-right mx-0">
                    <S.CardText bold xs md={9} className="px-0">
                      Your {brand} Novated Leasing Price
                    </S.CardText>
                    <S.CardValue
                      bold
                      xs
                      md={3}
                      className="px-0"
                      id="carsection.novatingleasingprice"
                    >
                      {formatCurrency(priveawayPrice)}
                    </S.CardValue>
                  </Row>
                )}
                {/* ACCESSORIES: End */}
                {/* Driving home your upfront savings: Start */}
                {gstCreditClaimable > 0 && (
                  <Row className="mt-4">
                    <E.DrivingHomeCol xs={12}>
                      {EstimateDetailStaticData('DrivingHomeTitle')}
                    </E.DrivingHomeCol>
                    <Col xs={12} id="carsection.gstsaving1">
                      {EstimateDetailStaticData('UpfrontSaving', {
                        saving: formatCurrency(gstCreditClaimable),
                      })}
                    </Col>
                    <E.YourGSTSaving xs={12} id="carsection.gstsaving2">
                      {EstimateDetailStaticData('DriveAwaySaving', {
                        saving: formatCurrency(gstCreditClaimable),
                      })}
                    </E.YourGSTSaving>
                  </Row>
                )}
                {/* Driving home your upfront savings: End */}
              </>
            </AccordionSection>
          </Card.Body>
        </Card>
        {vehicleType && vehicleType === 'New' && (
          <AccordionCard
            title="How soon will I get my car?"
            expandAccordion={expandAccordion}
            id="carsection.howsoonaccordion"
            className="card-shadow"
          >
            <S.BrandDefaultText>
              If the car you’ve chosen is in stock, you could be behind the
              wheel in 2 to 5 business days after signing your lease. Get a more
              accurate idea by asking your consultant about the timeline for the
              car you’ve got your eye on.
            </S.BrandDefaultText>
          </AccordionCard>
        )}
      </Container>
    </>
  )
}


export default CarSection



